import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

interface Company {
  id: string;
  name: string;
  registration_number: string | null;
  jurisdiction: string | null;
  created_at: string;
  user_count: number;
}

export function SuperAdminCompanyManagement() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);

  async function loadCompanies() {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('companies')
        .select(
          `
          id,
          name,
          registration_number,
          jurisdiction,
          created_at
        `
        )
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get user counts for each company
      const { data: userCounts, error: countError } = await supabase
        .from('user_roles')
        .select('company_id, user_id');

      if (countError) throw countError;

      const companiesWithCounts =
        data?.map((company) => ({
          ...company,
          user_count: userCounts?.filter((ur) => ur.company_id === company.id).length || 0,
        })) || [];

      setCompanies(companiesWithCounts);
    } catch (error: any) {
      toast.error('Failed to load companies: ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCompanies();
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Platform Companies</CardTitle>
            <CardDescription>View and manage all companies on the platform</CardDescription>
          </div>
          <Button onClick={loadCompanies} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading companies...</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company Name</TableHead>
                <TableHead>Registration</TableHead>
                <TableHead>Jurisdiction</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companies.map((company) => (
                <TableRow key={company.id}>
                  <TableCell className="font-medium">{company.name}</TableCell>
                  <TableCell>
                    {company.registration_number || (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {company.jurisdiction ? (
                      <Badge variant="outline">{company.jurisdiction}</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{company.user_count}</Badge>
                  </TableCell>
                  <TableCell>{new Date(company.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/companies/${company.id}`}>
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
